import { useCustom } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, ClipboardPlus, PackageSearch } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { MarkAll, MarkBar, MarkCell } from "@/components/Marks";
import { PodsortCreateSheet } from "@/components/PodsortCreateSheet";
import { query } from "@/lib/api";
import { boxWord, errorMessage, positionWord } from "@/lib/format";
import { supplyProduct } from "@/lib/links";
import { pageData } from "@/lib/loaded";
import {
  CHOICE_DEFAULT,
  CHOICES,
  PLATFORMS,
  choice,
  choiceParam,
  createBlocked,
  createItems,
  createUnits,
  fields,
  filterWords,
  ordersWords,
  platformOf,
  shelfName,
  shelfNumber,
  stockWords,
  tasksHref,
} from "@/lib/podsort";
import { DATA_QUERIES } from "@/lib/providers";
import type { Podsort, PodsortRow } from "@/lib/providers";
import {
  click,
  empty,
  joinList,
  lineList,
  picked,
  selectAll,
  type Selection,
} from "@/lib/select";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 300;

/** Сколько ждать после последней цифры нормы, прежде чем спрашивать склад. */
const TYPING_PAUSE = 500;

/**
 * Подсорт → Расчёт: какого товара не хватает витрине маркетплейса, сколько
 * собрать — и кнопка, которая ставит это задачами сборщику.
 *
 * С 17.09.26 раздел УНИВЕРСАЛЬНЫЙ (задача владельца): переключатель выбирает
 * ровно одну площадку — ВБ или Ozon. Площадка меняет ЧЬЮ полку спрашиваем
 * (отбор по полке и возврату), а всё, что про наши короба, — бронь FBS и
 * задачи подсорта — считается по всем площадкам: вещь заберут из того же
 * короба.
 *
 * Объект — БАРКОД, один размер одной модели: им товар лежит на полке, им же
 * его возьмёт сборщик.
 *
 * ТРИ ОТБОРА ПО ПОЛКЕ, потому что полка — три числа, и складывать их нельзя:
 * лежит на складе, уехало покупателю, едет возвратом. Отбор по умолчанию —
 * «на складе пусто»: такой товар не продаётся вовсе, пока лежит у нас.
 *
 * НОРМА — сколько штук везём на позицию, а не «до какого уровня дозаполняем».
 * Из неё вычитается то, что по этой позиции УЖЕ В РАБОТЕ у площадки (задачи и
 * короба подсорта): иначе «Создать» ставило бы одну и ту же штуку снова.
 *
 * ПОРЯДОК — ЗАКАЗЫ ЗА НЕДЕЛЮ ПО УБЫВАНИЮ (требование владельца 11.09.26).
 *
 * Весь отбор живёт в АДРЕСЕ, а не в состоянии экрана: ссылку «что подсортить в
 * Самаре на Ozon» отправляют сменщику, и открыться у него она обязана такой же.
 */
export function PodsortPage() {
  const [params, setParams] = useSearchParams();
  // Площадка в адресе только когда она НЕ умолчание: ссылка `/fbw?city=7`,
  // разосланная до переименования, обязана открыться той же — ВБ.
  const platform = platformOf(params.get("platform"));
  const cityParam = params.get("city") ?? "";
  const shelfParam = choice(params.get("shelf") ?? CHOICE_DEFAULT.shelf);
  const toParam = choice(params.get("to") ?? CHOICE_DEFAULT.to);
  // Возврат по умолчанию «нет» (решение владельца 11.09.26): к позиции, куда
  // едет возврат, дыра закроется и без нас.
  const fromParam = choice(params.get("from") ?? CHOICE_DEFAULT.from);
  const normParam = params.get("norm") ?? "1";
  const keepParam = params.get("keep") ?? "1";
  const limitParam = params.get("n") ?? "";

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена отбора возвращает список к первой странице: поднятый потолок
    // относился к прежнему отбору.
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  // ЧИСЛА НАБИРАЮТСЯ ЦИФРАМИ, а запрос стоит секунду, поэтому набранное
  // доезжает до адреса с паузой.
  const [normDraft, setNormDraft] = useDraft(normParam, (v) => set("norm", v), 1);
  const [keepDraft, setKeepDraft] = useDraft(keepParam, (v) => set("keep", v), 0);

  const url = useMemo(
    () =>
      `/api/podsort${query({
        platform: platform === "wb" ? undefined : platform,
        city_id: cityParam || undefined,
        shelf: shelfParam,
        to_client: toParam,
        from_client: fromParam,
        norm: normParam,
        keep: keepParam,
        limit: limitParam || undefined,
      })}`,
    [platform, cityParam, shelfParam, toParam, fromParam, normParam, keepParam, limitParam],
  );

  const listQuery = useCustom<Podsort>({ url, method: "get" });
  const queries = useQueryClient();
  useSessionGuard(listQuery.query.error);

  // Списком считается только payload, в котором ДЕЙСТВИТЕЛЬНО есть строки:
  // `result.data` у Refine существует и до загрузки — пустым объектом, и
  // обращение к `list.shelf.available` на нём уронило бы экран целиком (разбор
  // ловушки — в `lib/loaded.ts`).
  const list = pageData(listQuery.result.data, "rows");
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error ? errorMessage(listQuery.query.error) : "";

  const rows = useMemo(() => list?.rows ?? [], [list]);
  const order = useMemo(() => rows.map((r) => r.barcode), [rows]);

  // Отметка галочками. Ключ — сам БАРКОД: список пересортируется на следующем
  // снимке полки, и отметка по номеру строки переехала бы на чужой товар.
  const [sel, setSel] = useState<Selection<string>>(() => empty<string>());
  const [flash, setFlash] = useState<{ text: string; id: number } | null>(null);
  const [creating, setCreating] = useState(false);

  // Смена отбора меняет САМ СПИСОК, а отмечали видимое: оставить отметку
  // значило бы унести баркоды, которых на экране уже нет. Площадка — тоже
  // отбор: отмеченное для ВБ на Ozon означало бы другие числа.
  useEffect(
    () => setSel(empty<string>()),
    [platform, cityParam, shelfParam, toParam, fromParam, normParam, keepParam],
  );

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  const chosen = picked(rows, (r) => r.barcode, sel);
  const marked = chosen.length;
  const allMarked = marked > 0 && marked === rows.length;

  const pick = (
    key: string,
    e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean },
  ) =>
    // Shift, Ctrl и ⌘ означают здесь одно: «от той строки до этой». Механика
    // общая с «Коробами» и «Баркодом» и живёт в `lib/select.ts`.
    setSel((s) => click(s, order, key, e.shiftKey || e.ctrlKey || e.metaKey));

  /**
   * Копирует отмеченное СТОЛБЦОМ — каждое значение с новой строки, как в
   * разделе «Баркод»: список вставляют в таблицу ячейка за ячейкой.
   */
  const copy = async (
    label: string,
    value: (row: PodsortRow) => string,
    sep = "\n",
  ) => {
    const { text, count, skipped } =
      sep === "\n"
        ? lineList(chosen.map(value))
        : joinList(chosen.map(value), sep);
    if (count === 0) {
      setFlash({ text: `${label}: копировать нечего`, id: Date.now() });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Буфер закрыт политикой браузера — показываем список, чтобы его можно
      // было выделить руками. Молча терять его нельзя.
      window.prompt("Скопируйте список:", text);
    }
    setFlash({
      text: `${label}: ${count}` + (skipped ? ` · пустых ${skipped}` : ""),
      id: Date.now(),
    });
  };

  const cities = list?.cities ?? [];
  const norm = list?.filter.norm ?? 1;
  const keep = list?.filter.keep ?? 1;
  const reserveKnown = list?.reserve.known ?? true;
  const tasksKnown = list?.tasks.known ?? true;
  const statDays = list?.stat.days ?? 7;
  const shelf = shelfName(platform);

  // ЧТО УЕДЕТ В ЗАДАЧИ — отмеченные строки их числом «Собрать». Город
  // обязателен: задача соседа живёт в городе (правило — `lib/podsort.ts`).
  const items = createItems(chosen);
  const blocked = createBlocked(cityParam, items);
  const cityName =
    cities.find((c) => String(c.city_id) === cityParam)?.name ?? `город ${cityParam}`;

  return (
    <div
      className="flex h-full min-h-0 flex-col p-4"
      data-component="PodsortPage"
      data-annot="подсорт расчёт"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[15px] font-semibold">Подсорт · Расчёт</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            Что довезти на склад {shelf} из наших коробов. Отбор:{" "}
            <span className="text-fg">
              {filterWords(
                { shelf: shelfParam, to_client: toParam, from_client: fromParam },
                platform,
              )}
            </span>
            {" · "}
            {/* Чем сужен ОСТАТОК — рядом с тем, чем сужен список: бронь FBS и
                задачи подсорта вычтены или нет и сколько штук решено оставить
                в коробах. Неучтённое названо вслух — молчание читалось бы как
                «всё свободно». */}
            <span className={reserveKnown && tasksKnown ? "text-fg" : "text-err"}>
              {stockWords(keep, reserveKnown, tasksKnown)}
            </span>
            {/* ПОРЯДОК НАЗВАН ВСЛУХ: он невидим, пока о нём не сказать. */}
            {` · сверху — что чаще заказывают за ${statDays} дн`}
          </p>
        </div>

        {/* ПЛОЩАДКА — ровно одна (владелец 17.09.26: «две сразу нельзя
            выбрать»). Стоит отдельно от отбора, у заголовка: она меняет не
            число полки, а САМУ полку, и смена её — другой расчёт. */}
        <div
          role="group"
          aria-label="Площадка"
          data-annot="площадка"
          className="flex flex-none gap-1 rounded-[var(--radius-ui)] border border-border p-0.5"
        >
          {PLATFORMS.map((p) => (
            <button
              key={p.value}
              type="button"
              aria-pressed={platform === p.value}
              onClick={() => set("platform", p.value === "wb" ? "" : p.value)}
              className={
                "h-7 rounded-[var(--radius-ui)] px-3 text-[13px] " +
                (platform === p.value
                  ? "bg-fg font-medium text-bg"
                  : "text-fg-muted hover:bg-bg-hover hover:text-fg")
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ОТБОР ПО ПОЛКЕ: три числа — три переключателя. Кнопками, а не
          выпадающими списками: у отбора всего три значения, и ряд «любой ·
          есть · нет» читается взглядом и нажимается одним движением. */}
      <div
        data-annot="отбор"
        className="mb-4 flex flex-wrap items-end gap-x-5 gap-y-3 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
      >
        {fields(platform).map((field) => {
          const value =
            field.key === "shelf"
              ? shelfParam
              : field.key === "to_client"
                ? toParam
                : fromParam;
          const key =
            field.key === "shelf" ? "shelf" : field.key === "to_client" ? "to" : "from";
          return (
            <div key={field.key} data-annot={field.label}>
              <p className="mb-1 text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
                {field.label}
              </p>
              <div className="flex gap-1" title={field.title}>
                {CHOICES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => set(key, choiceParam(key, c.value))}
                    className={
                      "h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] " +
                      (value === c.value
                        ? "border-accent/60 bg-accent-dim text-accent-text"
                        : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
                    }
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {/* НОРМА ПОДСОРТА — сколько штук везём на позицию. */}
        <div data-annot="норма">
          <p className="mb-1 text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
            везём на позицию
          </p>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={1}
              max={100}
              value={normDraft}
              onChange={(e) => setNormDraft(e.target.value)}
              title="Сколько штук каждой позиции готовы отправить"
              aria-label="Норма подсорта, штук на позицию"
              className="h-7 w-[70px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] tnum focus:border-border-strong"
            />
            <span className="text-[12.5px] text-fg-muted">шт</span>
          </div>
        </div>

        {/* ЗАПАС СКЛАДА — сколько штук обязано остаться в коробах ПОСЛЕ
            подсорта (требование владельца 11.09.26). Ноль разрешён и значит
            «увозим хоть последнюю». */}
        <div data-annot="остаток">
          <p className="mb-1 text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
            оставить в коробах
          </p>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={100}
              value={keepDraft}
              onChange={(e) => setKeepDraft(e.target.value)}
              title="Сколько штук обязано остаться в коробах после подсорта"
              aria-label="Остаток в коробах после подсорта, штук"
              className="h-7 w-[70px] rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] tnum focus:border-border-strong"
            />
            <span className="text-[12.5px] text-fg-muted">шт</span>
          </div>
        </div>

        {/* ГОРОД — отбор по СКЛАДУ, а не по полке: сборщик в Воронеже не
            возьмёт штуку из Самары. И город же обязателен для «Создать»:
            задача подсорта живёт в городе. */}
        {cities.length > 0 ? (
          <div data-annot="город">
            <p className="mb-1 text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
              город
            </p>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => set("city", "")}
                className={
                  "h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] " +
                  (cityParam === ""
                    ? "border-accent/60 bg-accent-dim text-accent-text"
                    : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
                }
              >
                весь склад
              </button>
              {cities.map((c) => (
                <button
                  key={c.city_id}
                  type="button"
                  onClick={() => set("city", String(c.city_id))}
                  title={`${c.name}: ${c.lines} ${positionWord(c.lines)}, ${c.units} шт в ${c.boxes} ${boxWord(c.boxes)}`}
                  className={
                    "h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] " +
                    (cityParam === String(c.city_id)
                      ? "border-accent/60 bg-accent-dim text-accent-text"
                      : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
                  }
                >
                  {c.name} <span className="tnum font-medium text-fg">{c.lines}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px]"
        >
          <AlertTriangle size={14} className="text-err" />
          {/* Полка площадки недоступна — это 503 и честная фраза, а не пустой
              список: «подсортить нечего» успокаивало бы ровно тогда, когда не
              проверено ничего. */}
          <span>{error}</span>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!list ? (
        <p className="text-[13px] text-fg-muted">Считаю…</p>
      ) : (
        <>
          <dl
            data-annot="сводка"
            className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5"
            aria-busy={busy}
          >
            <Metric
              label="Требуют подсорта"
              value={list.total}
              unit={positionWord(list.total)}
              note={`из ${list.totals.scanned} позиций в коробах`}
            />
            {/* ГЛАВНОЕ ЧИСЛО РАЗДЕЛА: сколько штук поедет при этой норме. */}
            <Metric
              label="Собрать"
              value={list.totals.need}
              unit="шт"
              accent
              note={`по ${norm} шт на позицию · у нас ${list.totals.units} шт`}
            />
            {/* ПРИДЕРЖАНО — позиции, прошедшие отбор полки, из которых
                отдавать нечего. Без этого числа «подсортить нечего» читалось
                бы как «на складе пусто». */}
            <Metric
              label="Придержано"
              value={list.totals.held}
              unit={positionWord(list.totals.held)}
              note={
                list.totals.held
                  ? `${list.totals.held_units} шт: бронь или последние штуки`
                  : "отдать можно всё, что подошло"
              }
            />
            {/* УЖЕ В РАБОТЕ — позиции, чью норму закрывают задачи подсорта и
                короба подсорта этой площадки. Они ушли из списка не потому,
                что не нужны, а потому, что их уже везут. */}
            <Metric
              label="Уже в работе"
              value={list.totals.covered}
              unit={positionWord(list.totals.covered)}
              note={
                tasksKnown
                  ? `${list.totals.covered_units} шт в задачах и коробах подсорта`
                  : "задачи подсорта не прочитаны"
              }
            />
            {/* Час снимка чужой полки. Не украшение: совет «вези это» стоит
                ровно столько, сколько стоит его свежесть. */}
            <Metric
              label={`Остатки ${shelf}`}
              value={list.shelf.synced_at.slice(11)}
              unit={list.shelf.synced_at.slice(0, 10)}
              note={
                list.totals.unknown
                  ? `${list.totals.unknown} ${positionWord(list.totals.unknown)} не заведено на ${shelf}`
                  : `снимок остатков ${shelf}`
              }
            />
          </dl>

          {rows.length === 0 ? (
            <Empty>
              Под этот отбор подсортить нечего: из {list.totals.scanned} позиций
              склада ни одна не подошла.
              {list.totals.covered ? (
                <>
                  {" "}
                  Уже в работе {list.totals.covered}{" "}
                  {positionWord(list.totals.covered)} —{" "}
                  <Link
                    to={tasksHref(platform, cityParam)}
                    className="text-fg underline underline-offset-2"
                  >
                    открыть задачи
                  </Link>
                  .
                </>
              ) : null}
              {list.totals.held ? (
                <>
                  {" "}
                  Придержано {list.totals.held}{" "}
                  {positionWord(list.totals.held)}: там бронь или последние
                  штуки — их покажет «оставить в коробах» 0.
                </>
              ) : null}
            </Empty>
          ) : (
            <>
              {/* Отмеченное СТАВЯТ В РАБОТУ кнопкой «Создать» (задачи
                  подсорта в FBS) или уносят СПИСКОМ — столбцом, а не через
                  запятую: список вставляют в таблицу ячейка за ячейкой. */}
              <MarkBar
                marked={
                  marked > 0
                    ? `Отмечено ${marked} ${positionWord(marked)}`
                    : ""
                }
                allMarked={allMarked}
                hint="Отметьте позиции галочками: Shift или Ctrl по второй строке отметит всё между ними."
                flash={flash?.text ?? ""}
                onAll={() => setSel(allMarked ? empty<string>() : selectAll(order))}
                onNone={() => setSel(empty<string>())}
                actions={[
                  {
                    // СОЗДАТЬ — первая кнопка ряда: ради неё отмечают.
                    // Погашенная говорит причину подсказкой, а не молчит.
                    label: items.length
                      ? `Создать задачи · ${createUnits(items)} шт`
                      : "Создать задачи",
                    title:
                      blocked ||
                      `Поставить ${createUnits(items)} шт задачами подсорта ${shelf} в ${cityName}`,
                    annot: "создать задачи",
                    icon: <ClipboardPlus size={13} className="flex-none" />,
                    disabled: blocked !== "",
                    onClick: () => setCreating(true),
                  },
                  {
                    label: "Скопировать баркоды",
                    title: "Баркоды отмеченных позиций, каждый с новой строки",
                    annot: "скопировать баркоды",
                    onClick: () => copy("Баркоды", (r) => r.barcode),
                  },
                  {
                    label: "Скопировать баркод и сколько",
                    title:
                      "Баркод и число штук к сбору — две колонки, готовые к вставке в таблицу",
                    annot: "скопировать баркод и сколько",
                    onClick: () =>
                      copy("Баркод и сколько", (r) => `${r.barcode}\t${r.need}`),
                  },
                ]}
              />
              {/* Почему «Создать» погашена — СЛОВАМИ, когда уже есть что
                  создавать: подсказку при наведении на складе никто не ждёт. */}
              {marked > 0 && blocked ? (
                <p
                  data-annot="почему не создать"
                  className="mb-2 text-[12px] text-fg-muted"
                >
                  {blocked}
                </p>
              ) : null}

              <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
                <table
                  data-annot="таблица"
                  className="w-full border-collapse text-[13px]"
                >
                  <caption className="sr-only">
                    Позиции, которых не хватает на складе {shelf}
                  </caption>
                  <thead className="hidden md:table-header-group">
                    <tr>
                      <th
                        scope="col"
                        className="sticky top-0 z-10 w-9 border-b border-border bg-bg-elevated px-2.5 py-2 text-center"
                      >
                        <MarkAll
                          all={allMarked}
                          some={marked > 0}
                          title="Отметить все показанные позиции"
                          onToggle={() =>
                            setSel(allMarked ? empty<string>() : selectAll(order))
                          }
                        />
                      </th>
                      {[
                        "Товар",
                        "Где у нас",
                        "Штук",
                        "Бронь",
                        `Склад ${shelf}`,
                        "Клиенту",
                        "Возврат",
                        "Собрать",
                      ].map((title) => (
                        <th
                          key={title}
                          scope="col"
                          className={
                            "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg " +
                            (title === "Товар" || title === "Где у нас"
                              ? "text-left"
                              : "text-right")
                          }
                        >
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="block md:table-row-group">
                    {rows.map((row) => (
                      <tr
                        key={row.barcode}
                        data-barcode={row.barcode}
                        data-marked={sel.keys.has(row.barcode) || undefined}
                        // Адрес замечания — САМ БАРКОД, а не позиция в списке.
                        data-annot={`строка ${row.barcode}`}
                        className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                      >
                        <MarkCell
                          label={`Отметить позицию ${row.barcode}`}
                          on={sel.keys.has(row.barcode)}
                          onPick={(e) => pick(row.barcode, e)}
                        />
                        {/* ИМЕНИ ТОВАРА ЗДЕСЬ НЕТ (решение владельца
                            11.09.26): оно вещь не различает. Вместо него СПРОС:
                            сколько раз этот товар заказали за неделю. */}
                        <Cell label="Товар">
                          <span className="flex items-center gap-2">
                            <Photo row={row} />
                            <span className="flex min-w-0 flex-col">
                              <span className="flex min-w-0 items-center gap-1.5">
                                <Link
                                  to={`/barcode/${row.barcode}`}
                                  className="truncate font-medium tnum text-fg hover:text-accent-text"
                                  title={row.card?.name || row.barcode}
                                >
                                  {row.barcode}
                                </Link>
                                <Stat productId={row.product_id} />
                              </span>
                              <span className="text-[11px] text-fg-muted">
                                {row.card?.size ? `${row.card.size} · ` : ""}
                                <span
                                  className={
                                    row.orders === null
                                      ? "text-err"
                                      : row.orders > 0
                                        ? "text-fg"
                                        : ""
                                  }
                                  title={
                                    list.stat.scope === "sku"
                                      ? `Заказы ${shelf} по этому размеру (SKU)`
                                      : `Заказы ${shelf} по товару целиком, все размеры`
                                  }
                                >
                                  {ordersWords(row.orders, statDays)}
                                </span>
                              </span>
                            </span>
                          </span>
                        </Cell>
                        {/* ГДЕ БРАТЬ. Город здесь несущий: подсорт делают
                            руками, и позиция, лежащая только в Самаре,
                            воронежскому сборщику недоступна вовсе. */}
                        <Cell label="Где у нас">
                          <span className="flex flex-wrap gap-x-2 gap-y-0.5 text-[12px] text-fg-muted">
                            {row.by_city.map((c) => (
                              <span key={c.city_id} className="whitespace-nowrap">
                                {c.city}{" "}
                                <span className="tnum text-fg">{c.units}</span>
                              </span>
                            ))}
                          </span>
                        </Cell>
                        <Cell label="Штук" align="text-right tnum">
                          <span className="flex flex-col items-end">
                            <span>{row.units}</span>
                            <span className="whitespace-nowrap text-[11px] text-fg-muted">
                              в {row.boxes} {boxWord(row.boxes)}
                            </span>
                          </span>
                        </Cell>
                        {/* БРОНЬ: вещь ещё в коробе, но её уже ждут — сборка
                            FBS или задача подсорта ЛЮБОЙ площадки. Число стоит
                            рядом с остатком, потому что объясняет, почему
                            «собрать» меньше, чем «штук». Половина подсорта
                            подписана отдельно: «продано» и «наша работа» —
                            разные разговоры. */}
                        <Cell label="Бронь" align="text-right tnum">
                          <span
                            className={
                              "flex flex-col items-end " +
                              (!reserveKnown || !tasksKnown
                                ? "text-err"
                                : row.reserved + row.claimed > 0
                                  ? "text-fg"
                                  : "text-fg-muted")
                            }
                            title={
                              reserveKnown && tasksKnown
                                ? `FBS «Ждёт подбора»: ${row.reserved} шт · задачи подсорта всех площадок: ${row.claimed} шт`
                                : "База задач FBS недоступна: бронь не учтена"
                            }
                          >
                            <span>
                              {!reserveKnown || !tasksKnown
                                ? "?"
                                : shelfNumber(row.reserved + row.claimed)}
                            </span>
                            {row.claimed > 0 ? (
                              <span className="whitespace-nowrap text-[11px] text-fg-muted">
                                подсорт {row.claimed}
                              </span>
                            ) : null}
                          </span>
                        </Cell>
                        {/* ТРИ ЧИСЛА ПОЛКИ — тремя колонками, а не суммой. */}
                        <Cell label={`Склад ${shelf}`} align="text-right tnum">
                          <span
                            className={
                              row.shelf && row.shelf.amount === 0
                                ? "font-semibold text-accent-text"
                                : ""
                            }
                          >
                            {row.shelf ? shelfNumber(row.shelf.amount) : "не заведён"}
                          </span>
                        </Cell>
                        <Cell label="Клиенту" align="text-right tnum">
                          <span className="text-fg-muted">
                            {row.shelf ? shelfNumber(row.shelf.to_client) : "—"}
                          </span>
                        </Cell>
                        <Cell label="Возврат" align="text-right tnum">
                          <span className="text-fg-muted">
                            {row.shelf ? shelfNumber(row.shelf.from_client) : "—"}
                          </span>
                        </Cell>
                        {/* СОБРАТЬ — и ровно столько уедет в задачи. Под числом
                            — сколько по этой позиции УЖЕ в работе у площадки:
                            норма уменьшена на него, а не забыта. */}
                        <Cell label="Собрать" align="text-right tnum">
                          <span className="flex flex-col items-end">
                            <span className="font-semibold">{row.need}</span>
                            {row.planned > 0 ? (
                              <span
                                className="whitespace-nowrap text-[11px] text-fg-muted"
                                title={`Уже в задачах и коробах подсорта ${shelf}`}
                              >
                                в работе {row.planned}
                              </span>
                            ) : null}
                          </span>
                        </Cell>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {list.shown < list.total ? (
                  <div className="border-t border-border p-3 text-center">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => set("n", String(list.shown + PAGE))}
                      className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
                    >
                      {busy
                        ? "Гружу…"
                        : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
                    </button>
                    <p className="mt-1.5 text-[11.5px] text-fg-muted">
                      Показано {list.shown} из {list.total}
                    </p>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </>
      )}

      {creating ? (
        <PodsortCreateSheet
          platform={platform}
          city={{ city_id: Number(cityParam), name: cityName }}
          norm={norm}
          keep={keep}
          rows={chosen}
          items={items}
          onClose={() => setCreating(false)}
          onDone={() => {
            // Поставленные задачи меняют расчёт: позиции уходят в «уже в
            // работе». Отметка снимается — отмеченного в списке больше нет.
            // Перечитываются ИМЕННО ДАННЫЕ (`DATA_QUERIES`): голая инвалидация
            // размонтировала бы кабинет вместе с окном итога.
            setSel(empty<string>());
            void queries.invalidateQueries({ queryKey: DATA_QUERIES });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Число, которое НАБИРАЮТ цифрами: черновик рядом с адресом, а не вместо него.
 *
 * Запрос раздела стоит секунду, и без паузы «10» успело бы сходить на склад
 * сначала за единицей. В адрес уезжает только годное число.
 */
function useDraft(
  value: string,
  apply: (next: string) => void,
  min: number,
  max = 100,
) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => {
      const n = Number(draft);
      if (Number.isFinite(n) && n >= min && n <= max) apply(String(Math.trunc(n)));
    }, TYPING_PAUSE);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return [draft, setDraft] as const;
}

/**
 * Иконка статистики: тот же товар в системе закупок, НОВОЙ вкладкой — отбор и
 * отметки галочками стоят человеку нескольких нажатий, и уход со страницы
 * стёр бы их.
 */
function Stat({ productId }: { productId: number }) {
  const href = supplyProduct(productId);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Статистика товара в системе закупок — откроется в новой вкладке"
      aria-label="Статистика товара в системе закупок"
      data-annot="статистика закупок"
      className="flex-none text-fg-muted hover:text-accent-text"
    >
      <BarChart3 size={13} />
    </a>
  );
}

/**
 * Фотография товара вместо его имени в тесной строке: имя товар не различает,
 * а человек стоит над коробом с вещью в руках. Нет картинки — номер модели.
 */
function Photo({ row }: { row: PodsortRow }) {
  const model = row.card?.model_id ?? 0;
  const image = row.card?.image_url ?? "";
  return (
    <Link
      to={model ? `/products/${model}` : `/barcode/${row.barcode}`}
      onClick={(e) => e.stopPropagation()}
      title={row.card?.name || `Баркод ${row.barcode}`}
      data-annot="фото"
      className="flex h-[54px] w-[40px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg-subtle text-[10px] text-fg-muted"
    >
      {image ? (
        <img
          src={image}
          alt={row.card?.name ?? ""}
          referrerPolicy="no-referrer"
          loading="lazy"
          className="h-full w-full object-cover"
          onError={(e) => {
            // Битая ссылка на чужой картинке — обычное дело, и рамка с номером
            // модели честнее сломанного значка браузера.
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span className="tnum">{model || "—"}</span>
      )}
    </Link>
  );
}

/**
 * Ячейка таблицы, которая на узком экране становится строкой «подпись —
 * значение». Второго набора разметки под мобильный экран не появляется.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}

/** Плитка сводки. Форма общая с разделом «Короба». */
function Metric({
  label,
  value,
  unit,
  note,
  accent,
}: {
  label: string;
  value: string | number;
  unit: string;
  note: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
        <span className="ml-1 text-[13px] font-normal text-fg-muted">{unit}</span>
      </dd>
      {note ? <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      <PackageSearch size={20} className="mx-auto mb-2 text-fg-muted" />
      {children}
    </div>
  );
}
